import { html, LitElement } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined';
import { kwCarouselInteractiveStyles } from './kw-carousel-interactive-styles';

export class KwCarouselInteractive extends LitElement {
  static get is() {
    return 'kw-carousel-interactive';
  }

  static get styles() {
    return [kwCarouselInteractiveStyles];
  }

  static get properties() {
    return {
      /**
       * An array with this component children
       * @property
       * @type { Array }
       */
      items: {
        type: Array,
      },
      /**
       * The next arrow button is disabled when there are no next elements
       * @property
       * @type { Boolean }
       */
      disabledNext: {
        type: Boolean,
      },
      /**
       * The previous arrow button is disabled when there are no previous elements
       * @property
       * @type { Boolean }
       */
      disabledPrevious: {
        type: Boolean,
      },
      /**
       * The number of dot selected. This determines also the item to be selected
       * @property
       * @type { Number }
       */
      dotSelected: {
        type: Number,
      },
      /**
       * The left position in pixels where the container of carousel items must be set.
       * @property
       * @type { Number }
       */
      left: {
        type: Number,
      },
      /**
       * The dom element selected. The component is observing the change of this value in "update".
       * @property
       * @type { Object }
       */
      selected: {
        type: Object,
      },
    };
  }

  constructor() {
    super();
    this.items = [];
    this.left = 0;
    this.disabledNext = false;
    this.disabledPrevious = true;
    this.totalDots = 0;
    this.dotSelected = 0;
  }

  renderItems() {
    return html`${this.items.map((child, index) => html`
        <li 
          id="item-${index}" 
          class="kw-carousel-interactive__item elem-${index}" 
          aria-hidden="${!(this.dotSelected === index)}"
          selected="${ifDefined(this.dotSelected === index || undefined)}"
        >${child}</li>
      `)}
    `;
  }

  renderDots() {
    return html`${this.items.map((child, index) => html`
        <button 
          id="dot-${index}"
          class="kw-carousel-interactive__dot elem-${index}"
          selected="${ifDefined(this.dotSelected === index || undefined)}"
          @click="${this.handleSelectedWithDot}"
          aria-label="select ${index} item"
        ></button>
      `)}
    `;
  }

  render() {
    if (this.items.length === 0) {
      /* eslint-disable-next-line no-console */
      // console.warn('You need to insert some content as slot html items inside a kw-carousel-interactive to see this component');
      return '';
    }
    return html`
    <div class="kw-carousel-interactive">
      <div class="kw-carousel-interactive__items-container">
        <div class="kw-carousel-interactive__items-list-wrapper">
          <ul class="kw-carousel-interactive__items-list" style="left:${this.left}px">
              ${this.renderItems()}
          </ul>
        </div>
      </div>
      <div class="kw-carousel-interactive__buttons-wrapper">
        <div class="kw-carousel-interactive__buttons-container">
          <span class="kw-carousel-interactive__arrow-container">
            <button
              class="kw-carousel-interactive__arrow"
              @click="${this.goPrevious}"
              .disabled="${this.disabledPrevious}"
              aria-label="Go to previous element"
            ></button>
          </span>
          <div class="kw-carousel-interactive__dots-container">
            ${this.renderDots()}
          </div>
          <span class="kw-carousel-interactive__arrow-container">
            <button
              class="kw-carousel-interactive__arrow kw-carousel-interactive__arrow--right"
              @click="${this.goNext}"
              .disabled="${this.disabledNext}"
              aria-label="Go to next element"
            ></button>
          </span>
        </div>
      </div>
    </div>
    `;
  }

  async firstUpdated() {
    this.items = [...this.children];

    if (this.items.length > 0) {
      if (this.items.length > 4) {
        console.warn('Only the first 4 items inside a kw-carousel-interactive component will be displayed.');
      }
      this.items.splice(4);
      this.updateComplete.then(() => {
        this.wrapper = this.shadowRoot.querySelector('.kw-carousel-interactive__items-list-wrapper');
        this.carousel = this.shadowRoot.querySelector('.kw-carousel-interactive__items-list');
        this.dotsContainer = this.shadowRoot.querySelector('.kw-carousel-interactive__dots-container');
        this.totalDots = this.items.length;
        this.selectDefault();
        this.setSizeOfElements();
      });
    }
  }

  setSizeOfElements() {
    this.carouselWidth = this.shadowRoot.querySelector('.kw-carousel-interactive__items-container').offsetWidth;
    const carouselElements = this.shadowRoot.querySelectorAll('.kw-carousel-interactive__item');
    carouselElements.forEach((el) => el.style.setProperty('width', `${this.carouselWidth}px`));
    const maxheight = [...carouselElements].reduce((accumulated, el) => {
      const maxHeight = el.offsetHeight > accumulated ? el.offsetHeight : accumulated;
      return maxHeight;
    }, 0);
    this.wrapper.style.setProperty('height', `${maxheight}px`);
  }

  selectDefault() {
    if (this.carousel.firstElementChild && !this.selected) {
      this.selected = this.carousel.firstElementChild;
      this.carousel.firstElementChild.setAttribute('selected', '');
    }
  }

  selectNewItem(currentSelected, newSelected) {
    currentSelected.removeAttribute('selected');
    newSelected.setAttribute('selected', '');
    this.selected = newSelected;
  }

  goPrevious() {
    const currentSelected = this.selected;
    const previousElem = this.selected && this.selected.previousElementSibling;
    if (previousElem) {
      this.selectNewItem(currentSelected, previousElem);
      this.left += this.carouselWidth;
    }
  }

  goNext() {
    const currentSelected = this.selected;
    const nextElem = this.selected && this.selected.nextElementSibling;
    if (nextElem) {
      this.selectNewItem(currentSelected, nextElem);
      this.left += (-this.carouselWidth);
    }
  }

  setLeftPositionOfItemSelected(numberOfSelected) {
    if (numberOfSelected === 0) this.left = 0;
    if (numberOfSelected === 1) this.left = -this.carouselWidth;
    if (numberOfSelected === 2) this.left = -(this.carouselWidth * 2);
    if (numberOfSelected === 3) this.left = -(this.carouselWidth * 3);
  }

  handleSelectedWithDot(e) {
    e.preventDefault();
    const notNumbersRegExp = /((?![0-9]).)/g;
    const { target } = e;
    const numberOfSelected = parseInt(target.id.replace(notNumbersRegExp, ''), 10);
    this.dotSelected = numberOfSelected;
    this.selected = this.shadowRoot.getElementById(`item-${numberOfSelected}`);
    this.setLeftPositionOfItemSelected(numberOfSelected);
  }

  onSelectedChanged(currentElementSelected) {
    const notNumbersRegExp = /((?![0-9]).)/g;
    const numberOfSelected = parseInt(currentElementSelected.getAttribute('id').replace(notNumbersRegExp, ''), 10);
    this.dotSelected = numberOfSelected;
    this.disabledPrevious = numberOfSelected === 0 || false;
    this.disabledNext = numberOfSelected + 1 === this.totalDots || false;
  }

  updated(changedProperties) {
    const selectedChanged = changedProperties.has('selected');
    if (selectedChanged) {
      const currentElementSelected = this.shadowRoot.querySelector('.kw-carousel-interactive__item[selected]');
      this.onSelectedChanged(currentElementSelected);
    }
  }
}
